import { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import type { CreateTripData, UpdateTripData, TripCategory, PrivacyLevel } from '../types';
import { TRIP_CATEGORIES, PRIVACY_LEVELS } from '../types';

interface TripFormProps {
  initialValues?: Partial<CreateTripData>;
  onSubmit: (data: CreateTripData | UpdateTripData) => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  isSubmitting?: boolean;
}

export function TripForm({ initialValues, onSubmit, onCancel, isEditing = false, isSubmitting = false }: TripFormProps) {
  const [title, setTitle] = useState(initialValues?.title || '');
  const [description, setDescription] = useState(initialValues?.description || '');
  const [startDate, setStartDate] = useState(initialValues?.startDate || '');
  const [endDate, setEndDate] = useState(initialValues?.endDate || '');
  const [category, setCategory] = useState<TripCategory>(initialValues?.category || 'leisure');
  const [privacy, setPrivacy] = useState<PrivacyLevel>(initialValues?.privacy || 'public');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Titulo e obrigatório');
      return;
    }
    if (!startDate) {
      setError('Data de inicio e obrigatoria');
      return;
    }
    if (!endDate) {
      setError('Data de fim e obrigatoria');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError('Data de fim deve ser posterior a data de inicio');
      return;
    }

    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        startDate,
        endDate,
        category,
        privacy,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar trip');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}

      <TextField
        label="Titulo"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
        fullWidth
        inputProps={{ maxLength: 100 }}
      />

      <TextField
        label="Descrição"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={3}
        fullWidth
        inputProps={{ maxLength: 1000 }}
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="Data de Inicio"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Data de Fim"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          required
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <TextField
        select
        label="Categoria"
        value={category}
        onChange={(e) => setCategory(e.target.value as TripCategory)}
        fullWidth
      >
        {TRIP_CATEGORIES.map((cat) => (
          <MenuItem key={cat.value} value={cat.value}>
            {cat.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Privacidade"
        value={privacy}
        onChange={(e) => setPrivacy(e.target.value as PrivacyLevel)}
        fullWidth
      >
        {PRIVACY_LEVELS.map((level) => (
          <MenuItem key={level.value} value={level.value}>
            {level.label}
          </MenuItem>
        ))}
      </TextField>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar Trip'}
        </Button>
      </Box>
    </Box>
  );
}
